import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminSettingsAPI } from '../services/api';
import { DriverPricingSlab, DriverIncentiveRange } from '../types';
import { Save, RefreshCw, Percent, Moon, Navigation, ArrowLeft, Plus, Trash2, Clock } from 'lucide-react';

const AdminPricingPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'roundTrip' | 'oneWay'>('roundTrip');

  // Pricing state with separate slabs for round trip and one way
  const [roundTripSlabs, setRoundTripSlabs] = useState<DriverPricingSlab[]>([]);
  const [oneWaySlabs, setOneWaySlabs] = useState<DriverPricingSlab[]>([]);
  
  const [platformFeePercentage, setPlatformFeePercentage] = useState<number | undefined>(undefined);
  const [taxPercentage, setTaxPercentage] = useState<number | undefined>(undefined);
  const [driverRequestTimeout, setDriverRequestTimeout] = useState(5);
  const [incentiveRanges, setIncentiveRanges] = useState<DriverIncentiveRange[]>([]);
  const [activeSection, setActiveSection] = useState<'pricing' | 'incentives'>('pricing');

  useEffect(() => {
    // Check if user is admin
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    if (user) {
      fetchPricing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await adminSettingsAPI.getSettings();
      if (response.data.driverPricing) {
        const driverPricing = response.data.driverPricing;
        
        // Load round trip and one way slabs
        if (Array.isArray(driverPricing.roundTripSlabs) && driverPricing.roundTripSlabs.length > 0) {
          setRoundTripSlabs(driverPricing.roundTripSlabs);
        }
        if (Array.isArray(driverPricing.oneWaySlabs) && driverPricing.oneWaySlabs.length > 0) {
          setOneWaySlabs(driverPricing.oneWaySlabs);
        }

        // Fallback: if old structure exists, migrate to round trip
        const driverPricingAny = driverPricing as any;
        if (!driverPricing.roundTripSlabs && Array.isArray(driverPricingAny.slabs) && driverPricingAny.slabs.length > 0) {
          setRoundTripSlabs(driverPricingAny.slabs);
        }
        
        setPlatformFeePercentage(driverPricing.platformFeePercentage);
        setTaxPercentage(driverPricing.taxPercentage);
      }

      // Load incentive ranges
      if (Array.isArray(response.data.driverIncentiveRanges) && response.data.driverIncentiveRanges.length > 0) {
        setIncentiveRanges(response.data.driverIncentiveRanges);
      } else {
        // Default incentive ranges
        setIncentiveRanges([
          { minRides: 1, maxRides: 5, amount: 300 },
          { minRides: 6, maxRides: 10, amount: 400 },
          { minRides: 11, maxRides: null, amount: 500 }
        ]);
      }
      
      if (response.data.driverRequestTimeout) {
        setDriverRequestTimeout(response.data.driverRequestTimeout);
      }
    } catch (err: any) {
      console.error('Error fetching pricing:', err);
      setError('Failed to load pricing settings');
    } finally {
      setLoading(false);
    }
  };

  const validateSlabs = (slabs: DriverPricingSlab[], type: string): boolean => {
    const sortedSlabs = [...slabs].sort((a, b) => a.minDistance - b.minDistance);

    for (let i = 0; i < sortedSlabs.length; i++) {
      const slab = sortedSlabs[i];
      
      if (i === 0 && slab.minDistance !== 0) {
        setError(`${type}: First slab must start at 0 km`);
        return false;
      }
      
      if (slab.minDistance >= slab.maxDistance) {
        setError(`${type} Slab ${i + 1}: Min distance must be less than max distance`);
        return false;
      }
      
      if (slab.maxTotalDistance !== undefined && slab.maxTotalDistance < slab.maxDistance) {
        setError(`${type} Slab ${i + 1}: Max total distance must be >= max distance`);
        return false;
      }
      
      if (i > 0) {
        const prevSlab = sortedSlabs[i - 1];
        if (slab.minDistance !== prevSlab.maxDistance) {
          slab.minDistance = prevSlab.maxDistance;
        }
      }
    }
    return true;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');
      setError('');

      if (activeSection === 'pricing') {
        // Validate both slab sets
        if (!validateSlabs(roundTripSlabs, 'Round Trip')) {
          setSaving(false);
          return;
        }
        if (!validateSlabs(oneWaySlabs, 'One Way')) {
          setSaving(false);
          return;
        }

        const sortedRoundTripSlabs = [...roundTripSlabs].sort((a, b) => a.minDistance - b.minDistance);
        const sortedOneWaySlabs = [...oneWaySlabs].sort((a, b) => a.minDistance - b.minDistance);
        
        const pricingData = {
          roundTripSlabs: sortedRoundTripSlabs,
          oneWaySlabs: sortedOneWaySlabs,
          platformFeePercentage,
          taxPercentage
        };
        
        await adminSettingsAPI.updateDriverPricing(pricingData);
        
        const settingsUpdate: any = {
          driverRequestTimeout
        };
        
        await adminSettingsAPI.updateSettings(settingsUpdate);
        
        setRoundTripSlabs(sortedRoundTripSlabs);
        setOneWaySlabs(sortedOneWaySlabs);
        setMessage('Pricing updated successfully!');
      } else {
        // Save incentive ranges
        const sortedRanges = [...incentiveRanges].sort((a, b) => a.minRides - b.minRides);
        
        // Validate ranges
        for (let i = 0; i < sortedRanges.length; i++) {
          const range = sortedRanges[i];
          if (range.minRides < 1) {
            setError(`Range ${i + 1}: Min rides must be >= 1`);
            setSaving(false);
            return;
          }
          if (range.maxRides !== null && range.maxRides < range.minRides) {
            setError(`Range ${i + 1}: Max rides must be >= min rides`);
            setSaving(false);
            return;
          }
          if (i > 0) {
            const prevRange = sortedRanges[i - 1];
            const prevMax = prevRange.maxRides || 999999;
            if (range.minRides <= prevMax) {
              setError(`Range ${i + 1}: Min rides must be > previous range max rides`);
              setSaving(false);
              return;
            }
          }
        }

        await adminSettingsAPI.updateDriverIncentiveRanges({ driverIncentiveRanges: sortedRanges });
        setIncentiveRanges(sortedRanges);
        setMessage('Incentive ranges updated successfully!');
      }
      
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      console.error('Error saving:', err);
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getCurrentSlabs = () => activeTab === 'roundTrip' ? roundTripSlabs : oneWaySlabs;
  const setCurrentSlabs = (slabs: DriverPricingSlab[]) => {
    if (activeTab === 'roundTrip') {
      setRoundTripSlabs(slabs);
    } else {
      setOneWaySlabs(slabs);
    }
  };

  const handleSlabChange = (index: number, field: keyof DriverPricingSlab, value: number | null | undefined) => {
    const current = getCurrentSlabs();
    const updated = [...current];
    updated[index] = { ...updated[index], [field]: value as any };
    setCurrentSlabs(updated);
  };

  const addSlab = () => {
    const current = getCurrentSlabs();
    const lastSlab = current[current.length - 1];
    const newSlab: DriverPricingSlab = {
      minDistance: lastSlab?.maxDistance || 0,
      maxDistance: (lastSlab?.maxDistance || 0) + 50,
      maxTotalDistance: undefined,
      fixedCharge: 0,
      ratePerKm: 0,
      nightlyRate: 450,
      overtimeHours: null,
      overtimeRatePerHour: null,
      nightDriveCharge: null
    };
    setCurrentSlabs([...current, newSlab]);
  };

  const handleIncentiveRangeChange = (index: number, field: keyof DriverIncentiveRange, value: number | null) => {
    const updated = [...incentiveRanges];
    updated[index] = { ...updated[index], [field]: value as any };
    setIncentiveRanges(updated);
  };

  const addIncentiveRange = () => {
    const lastRange = incentiveRanges[incentiveRanges.length - 1];
    const newRange: DriverIncentiveRange = {
      minRides: lastRange ? (lastRange.maxRides || lastRange.minRides) + 1 : 1,
      maxRides: lastRange ? (lastRange.maxRides || lastRange.minRides) + 5 : 5,
      amount: 0
    };
    setIncentiveRanges([...incentiveRanges, newRange]);
  };

  const removeIncentiveRange = (index: number) => {
    if (incentiveRanges.length <= 1) {
      alert('You must have at least one incentive range');
      return;
    }
    setIncentiveRanges(incentiveRanges.filter((_, i) => i !== index));
  };

  const removeSlab = (index: number) => {
    const current = getCurrentSlabs();
    if (current.length <= 1) {
      setError('You must have at least one slab');
      setTimeout(() => setError(''), 3000);
      return;
    }
    setCurrentSlabs(current.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-black mx-auto mb-4" />
          <p className="text-gray-300">Loading pricing settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-gray-300 hover:text-white mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Admin Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Navigation className="h-8 w-8 text-black mr-3" />
                Driver Pricing Management
              </h1>
              <p className="text-gray-600 mt-2">Configure slab-based pricing for driver services</p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <>
                  <RefreshCw className="h-5 w-5 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5 mr-2" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
            <p className="text-green-800">{message}</p>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Driver Request Timeout */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-4">
              <Clock className="h-5 w-5 text-black mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Driver Request Settings</h3>
            </div>
            <div className="max-w-md">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Request Timeout (minutes)
              </label>
              <input
                type="number"
                value={driverRequestTimeout}
                onChange={(e) => setDriverRequestTimeout(Math.max(1, Math.min(60, parseInt(e.target.value) || 5)))}
                min="1"
                max="60"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum time drivers have to accept a request (1-60 minutes)
              </p>
            </div>
          </div>

          {/* Main Section Selector */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex space-x-2 border-b border-gray-200">
              <button
                onClick={() => setActiveSection('pricing')}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeSection === 'pricing'
                    ? 'border-b-2 border-black text-black'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Pricing
              </button>
              <button
                onClick={() => setActiveSection('incentives')}
                className={`px-6 py-3 font-medium transition-colors ${
                  activeSection === 'incentives'
                    ? 'border-b-2 border-black text-black'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Incentives
              </button>
            </div>
          </div>

          {activeSection === 'pricing' ? (
            <>
              {/* Trip Type Tabs */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex space-x-2 mb-4">
                  <button
                    onClick={() => setActiveTab('roundTrip')}
                    className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === 'roundTrip'
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Round Trip
              </button>
              <button
                onClick={() => setActiveTab('oneWay')}
                className={`flex-1 px-4 py-2 rounded-md font-medium transition-colors ${
                  activeTab === 'oneWay'
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                One Way
              </button>
            </div>
          </div>

          {/* Dynamic Slabs */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Navigation className="h-5 w-5 text-black mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeTab === 'roundTrip' ? 'Round Trip' : 'One Way'} Distance Slabs
                </h3>
              </div>
              <button
                onClick={addSlab}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center text-sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Slab
              </button>
            </div>

            <div className="space-y-4">
              {getCurrentSlabs().map((slab, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 relative">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">
                      Slab {index + 1}: {slab.minDistance}-{slab.maxDistance === 999999 ? '∞' : slab.maxDistance} km
                    </h4>
                    {getCurrentSlabs().length > 1 && (
                      <button
                        onClick={() => removeSlab(index)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Remove slab"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Min Distance (km)
                      </label>
                      <input
                        type="number"
                        value={slab.minDistance}
                        onChange={(e) => handleSlabChange(index, 'minDistance', Math.max(0, parseFloat(e.target.value) || 0))}
                        disabled={index === 0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                      {index === 0 && (
                        <p className="text-xs text-gray-500 mt-1">First slab must start at 0</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Distance (km)
                      </label>
                      <input
                        type="number"
                        value={slab.maxDistance === 999999 ? '' : slab.maxDistance}
                        onChange={(e) => handleSlabChange(index, 'maxDistance', parseFloat(e.target.value) || 999999)}
                        placeholder="∞"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fixed Charge (₹)
                      </label>
                      <input
                        type="number"
                        value={slab.fixedCharge}
                        onChange={(e) => handleSlabChange(index, 'fixedCharge', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rate Per KM (₹)
                      </label>
                      <input
                        type="number"
                        value={slab.ratePerKm}
                        onChange={(e) => handleSlabChange(index, 'ratePerKm', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">For variable pricing</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Moon className="inline h-4 w-4 mr-1" />
                        Per Night Rate (₹)
                      </label>
                      <input
                        type="number"
                        value={slab.nightlyRate}
                        onChange={(e) => handleSlabChange(index, 'nightlyRate', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                    </div>
                  </div>

                  {/* Additional Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Total Distance (km)
                      </label>
                      <input
                        type="number"
                        value={slab.maxTotalDistance || ''}
                        onChange={(e) => handleSlabChange(index, 'maxTotalDistance', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum total distance for this slab</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Overtime Hours
                      </label>
                      <input
                        type="number"
                        value={slab.overtimeHours || ''}
                        onChange={(e) => handleSlabChange(index, 'overtimeHours', e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Hours after which overtime applies</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Overtime Rate Per Hour (₹)
                      </label>
                      <input
                        type="number"
                        value={slab.overtimeRatePerHour || ''}
                        onChange={(e) => handleSlabChange(index, 'overtimeRatePerHour', e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Night Drive Charge (₹)
                      </label>
                      <input
                        type="number"
                        value={slab.nightDriveCharge || ''}
                        onChange={(e) => handleSlabChange(index, 'nightDriveCharge', e.target.value === '' ? null : parseFloat(e.target.value))}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Charge for trips between 11 PM - 6 AM</p>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    {slab.fixedCharge > 0 && slab.ratePerKm > 0 && (
                      <span className="text-yellow-600">⚠️ Both fixed charge and per-km rate are set. Fixed charge will be used.</span>
                    )}
                    {slab.fixedCharge === 0 && slab.ratePerKm === 0 && (
                      <span className="text-red-600">⚠️ Either fixed charge or per-km rate must be set.</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform Fees */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-4">
              <Percent className="h-5 w-5 text-black mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Platform Fees & Taxes</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platform Fee (%)
                </label>
                <input
                  type="number"
                  value={platformFeePercentage !== undefined ? platformFeePercentage : ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      setPlatformFeePercentage(Math.max(0, Math.min(100, value)));
                    } else {
                      setPlatformFeePercentage(undefined);
                    }
                  }}
                  min="0"
                  max="100"
                  placeholder="Enter platform fee percentage"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Percentage of subtotal charged as platform fee</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST/Tax (%)
                </label>
                <input
                  type="number"
                  value={taxPercentage !== undefined ? taxPercentage : ''}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value)) {
                      setTaxPercentage(Math.max(0, Math.min(100, value)));
                    } else {
                      setTaxPercentage(undefined);
                    }
                  }}
                  min="0"
                  max="100"
                  placeholder="Enter GST/tax percentage"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Percentage of subtotal charged as GST/tax</p>
              </div>
            </div>
          </div>

              {/* Pricing Formula Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-2">💡 Pricing Formula</h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Distance Cost:</strong> Fixed Charge OR (Distance × Rate Per KM)</p>
                  <p><strong>Nightly Cost:</strong> Number of Nights × Per Night Rate</p>
                  <p><strong>Subtotal:</strong> Distance Cost + Nightly Cost</p>
                  <p><strong>Platform Fee:</strong> Subtotal × Platform Fee %</p>
                  <p><strong>Tax:</strong> Subtotal × GST/Tax %</p>
                  <p><strong>Total:</strong> Subtotal + Platform Fee + Tax</p>
                </div>
              </div>
            </>
          ) : (
            /* Incentive Management Section */
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <Navigation className="h-5 w-5 text-black mr-2" />
                  <h3 className="text-lg font-semibold text-gray-900">Driver Incentive Ranges</h3>
                </div>
                <button
                  onClick={addIncentiveRange}
                  className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-colors flex items-center"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Range
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Configure incentive amounts for different ride ranges. For example: Rides 1-5 = ₹300, Rides 6-10 = ₹400, etc.
              </p>

              <div className="space-y-4">
                {incentiveRanges.map((range, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-gray-900">
                        Range {index + 1}: Rides {range.minRides} - {range.maxRides === null ? '∞' : range.maxRides}
                      </h4>
                      {incentiveRanges.length > 1 && (
                        <button
                          onClick={() => removeIncentiveRange(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Rides
                        </label>
                        <input
                          type="number"
                          value={range.minRides}
                          onChange={(e) => {
                            const value = Math.max(1, parseInt(e.target.value) || 1);
                            handleIncentiveRangeChange(index, 'minRides', value);
                          }}
                          min="1"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Rides (leave empty for unlimited)
                        </label>
                        <input
                          type="number"
                          value={range.maxRides === null ? '' : range.maxRides}
                          onChange={(e) => {
                            const value = e.target.value === '' ? null : Math.max(1, parseInt(e.target.value) || 1);
                            handleIncentiveRangeChange(index, 'maxRides', value);
                          }}
                          min="1"
                          placeholder="Unlimited"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Incentive Amount (₹)
                        </label>
                        <input
                          type="number"
                          value={range.amount}
                          onChange={(e) => {
                            const value = Math.max(0, parseFloat(e.target.value) || 0);
                            handleIncentiveRangeChange(index, 'amount', value);
                          }}
                          min="0"
                          step="0.01"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gray-500"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPricingPage;
